import { gql } from '@apollo/client'
import { useApolloClient } from '@apollo/client/react'
import { useCallback, useEffect } from 'react'

const TTL_IN_MS = 5 * 60 * 1000

export enum UploadType {
  startPageAsset = 'startPageAsset',
  postAsset = 'postAsset',
  channelAvatar = 'channelAvatar',
  accountAvatar = 'accountAvatar',
  clientLogo = 'clientLogo',
}

export const isValidUploadType = (val: string): val is UploadType => {
  return val in UploadType
}

export type UserS3UploadPreSignedURLResponse = {
  url: string
  key: string
  bucket: string
}

export const GET_S3_PRE_SIGNED_URL = gql`
  query s3PreSignedURL($input: S3PreSignedURLInput!) {
    s3PreSignedURL(input: $input) {
      url
      key
      bucket
    }
  }
`

export type S3PreSignedURLInput = {
  organizationId: string
  uploadType: UploadType
  fileName: string
  mimeType: string
}

export function useS3PreSignedURL(): ({
  organizationId,
  fileName,
  mimeType,
  uploadType,
}: S3PreSignedURLInput) => Promise<UserS3UploadPreSignedURLResponse> {
  const client = useApolloClient()

  useEffect(() => {
    const interval = setInterval(() => {
      client.cache.evict({
        fieldName: 's3PreSignedURL',
      })
    }, TTL_IN_MS)

    return (): void => {
      client.cache.evict({
        fieldName: 's3PreSignedURL',
      })
      clearInterval(interval)
    }
  }, [client.cache])

  return useCallback(
    ({
      organizationId,
      fileName,
      mimeType,
      uploadType,
    }: S3PreSignedURLInput): Promise<UserS3UploadPreSignedURLResponse> =>
      client
        .query<{ s3PreSignedURL: UserS3UploadPreSignedURLResponse }>({
          query: GET_S3_PRE_SIGNED_URL,
          variables: {
            input: {
              organizationId,
              fileName,
              mimeType,
              uploadType,
            },
          },
        })
        .then((result) => {
          if (!result.data) {
            throw new Error('s3PreSignedURL query returned no data')
          }

          return result.data.s3PreSignedURL
        }),
    [client],
  )
}
